import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, BookOpen, Sparkles } from "lucide-react";
import api, { API_BASE } from "../lib/api";

export default function Manual() {
    const [manual, setManual] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/manual")
            .then(({ data }) => setManual(data))
            .finally(() => setLoading(false));
        document.title = "LovinLife — User Manual";
    }, []);

    if (loading || !manual) {
        return <div className="grid min-h-[60vh] place-items-center bg-obsidian text-gold">Loading manual…</div>;
    }

    return (
        <div className="bg-obsidian" data-testid="manual-page">
            {/* Header */}
            <section className="border-b border-border-dim">
                <div className="mx-auto max-w-5xl px-6 py-16 lg:py-24">
                    <p className="text-[11px] uppercase tracking-[0.4em] text-gold">
                        <Sparkles className="mr-2 inline h-3 w-3" /> {manual.version}
                    </p>
                    <h1 className="mt-3 font-display text-4xl text-white sm:text-6xl">{manual.title}</h1>
                    <p className="mt-4 max-w-2xl text-base text-white/60 sm:text-lg">{manual.tagline}</p>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <a
                            href={`${API_BASE}/manual/pdf`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="manual-download-pdf"
                            className="shine inline-flex items-center gap-3 bg-gold px-6 py-3 text-[12px] uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-gold-light"
                        >
                            <Download className="h-4 w-4" /> Download PDF
                        </a>
                        <a
                            href="#contents"
                            className="inline-flex items-center gap-2 border border-border-dim px-6 py-3 text-[12px] uppercase tracking-[0.3em] text-white/70 transition-colors hover:border-gold hover:text-gold"
                        >
                            <BookOpen className="h-4 w-4" /> Jump to contents
                        </a>
                    </div>
                </div>
            </section>

            <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 px-6 py-16 lg:grid-cols-[1fr_3fr] lg:py-20">
                {/* Sticky contents */}
                <aside id="contents" className="lg:sticky lg:top-28 lg:self-start">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Contents</p>
                    <ol className="mt-4 space-y-2 text-sm">
                        {manual.sections.map((sec, i) => (
                            <li key={sec.title}>
                                <a
                                    href={`#section-${i}`}
                                    className="group flex items-baseline gap-3 text-white/70 transition-colors hover:text-gold"
                                >
                                    <span className="font-display text-xs text-gold/80">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span>{sec.title}</span>
                                </a>
                            </li>
                        ))}
                    </ol>
                    <div className="mt-8 hidden border-t border-border-dim pt-6 lg:block">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Quick links</p>
                        <ul className="mt-3 space-y-2 text-sm">
                            <li>
                                <Link to="/admin" className="text-white/70 hover:text-gold">
                                    → Admin dashboard
                                </Link>
                            </li>
                            <li>
                                <Link to="/admin/content" className="text-white/70 hover:text-gold">
                                    → Edit site content
                                </Link>
                            </li>
                            <li>
                                <Link to="/admin/products" className="text-white/70 hover:text-gold">
                                    → Manage products
                                </Link>
                            </li>
                            <li>
                                <Link to="/admin/orders" className="text-white/70 hover:text-gold">
                                    → Orders
                                </Link>
                            </li>
                        </ul>
                    </div>
                </aside>

                {/* Sections */}
                <div className="space-y-16">
                    {manual.sections.map((sec, i) => (
                        <section
                            key={sec.title}
                            id={`section-${i}`}
                            className="scroll-mt-28 border-l border-border-dim pl-6 sm:pl-10"
                        >
                            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                                {String(i + 1).padStart(2, "0")} · LovinLife
                            </p>
                            <h2 className="mt-2 font-display text-3xl text-white sm:text-5xl">{sec.title}</h2>
                            {sec.intro && (
                                <p className="mt-4 text-base italic text-white/60 sm:text-lg">{sec.intro}</p>
                            )}
                            <div className="mt-8 space-y-5">
                                {(sec.blocks || []).map((b, j) => (
                                    <Block key={j} block={b} />
                                ))}
                            </div>
                        </section>
                    ))}

                    <div className="border-t border-border-dim pt-8 text-center">
                        <p className="font-display text-xl text-white">
                            <span className="text-heart">♥</span> Lovin life — every day.
                        </p>
                        <a
                            href={`${API_BASE}/manual/pdf`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-6 inline-flex items-center gap-2 border border-gold px-6 py-3 text-[11px] uppercase tracking-[0.3em] text-gold hover:bg-gold hover:text-obsidian"
                        >
                            <Download className="h-4 w-4" /> Take it with you · PDF
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Block({ block }) {
    if (block.type === "p") {
        return <p className="text-base leading-relaxed text-white/80">{block.text}</p>;
    }
    if (block.type === "h") {
        return <h3 className="font-display text-xl text-gold sm:text-2xl">{block.text}</h3>;
    }
    if (block.type === "ul") {
        return (
            <ul className="space-y-2 text-base leading-relaxed text-white/80">
                {block.items.map((it, k) => (
                    <li key={k} className="flex gap-3">
                        <span className="mt-2 inline-block h-1 w-1 flex-shrink-0 bg-gold" />
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        );
    }
    if (block.type === "ol") {
        return (
            <ol className="space-y-2 text-base leading-relaxed text-white/80">
                {block.items.map((it, k) => (
                    <li key={k} className="flex gap-3">
                        <span className="font-display text-sm text-gold">{String(k + 1).padStart(2, "0")}</span>
                        <span>{it}</span>
                    </li>
                ))}
            </ol>
        );
    }
    if (block.type === "tip") {
        return (
            <div className="border-l-2 border-gold bg-surface px-5 py-4">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Tip</p>
                <p className="mt-2 text-sm italic text-white/80">{block.text}</p>
            </div>
        );
    }
    return null;
}
